import { Component } from "@theme/component";
import { mediaHoverFine, prefersReducedMotion } from "@theme/utilities";

class VideoCard extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #hoverIntentTimer = null;

  /** @type {boolean} whether the current playback was started by an explicit click, not just a hover preview */
  #explicitPlay = false;

  /** @type {number} timestamp of the last click inside this card, to tell user pauses apart from external ones */
  #lastClickAt = 0;

  /** @type {boolean} whether a marketing popup (Attentive) paused this video */
  #pausedByPopup = false;

  /** @type {number} how many times we've auto-resumed while the popup is open */
  #popupResumeAttempts = 0;

  /** @type {MutationObserver | null} watches for the Attentive popup being removed */
  #popupObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener("click", this.#onCenterClick, { signal });

    // Media "pause" events don't bubble; catch them in the capture phase so we
    // can undo pauses caused by third-party popups (e.g. the Attentive wheel).
    this.addEventListener("pause", this.#onVideoPause, { capture: true, signal });
    document.addEventListener("fullscreenchange", this.#onFullscreenChange, { signal });

    this.#popupObserver = new MutationObserver(this.#onBodyMutation);
    this.#popupObserver.observe(document.body, { childList: true });

    // Respect reduced motion: do not autoplay on hover/focus.
    if (prefersReducedMotion()) return;

    // Only bind hover autoplay for pointer-fine devices.
    if (!mediaHoverFine()) return;

    this.addEventListener("mouseenter", this.#onEnter, { signal });
    this.addEventListener("mouseleave", this.#onLeave, { signal });
    this.addEventListener("focusin", this.#onEnter, { signal });
    this.addEventListener("focusout", this.#onFocusOut, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearHoverIntent();
    this.#popupObserver?.disconnect();
    this.#popupObserver = null;
  }

  get deferredMedia() {
    return this.querySelector("deferred-media");
  }

  get content() {
    return this.querySelector(".video-card__content");
  }

  #clearHoverIntent() {
    if (this.#hoverIntentTimer) {
      window.clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
    }
  }

  #onEnter = () => {
    this.#clearHoverIntent();

    // Small delay avoids accidental scrubs when moving pointer across a grid.
    this.#hoverIntentTimer = window.setTimeout(() => {
      this.#setActive(true);
      void this.#play();
    }, 120);
  };

  #onLeave = () => {
    this.#clearHoverIntent();
    this.#setActive(false);

    // Don't cut off a video the user explicitly chose to play; only auto-pause
    // the muted hover preview.
    if (!this.#explicitPlay) {
      this.#pause();
    }
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof HTMLElement && this.contains(next)) return;
    this.#onLeave();
  };

  #setActive(isActive) {
    this.classList.toggle("is-hovering", isActive);
    this.content?.classList.toggle("is-hidden", isActive);
  }

  async #play() {
    const dm = this.deferredMedia;
    if (!dm) return;

    // If poster exists and media isn't loaded, load + play.
    if (!dm.hasAttribute("data-media-loaded")) {
      await dm.showDeferredMedia?.();
      return;
    }

    await dm.playMedia?.();
  }

  #pause() {
    const dm = this.deferredMedia;
    if (!dm) return;
    dm.pauseMedia?.();
  }

  #unmute(video) {
    const dm = this.deferredMedia;
    if (!video || !dm) return;
    video.muted = false;
    dm.updateVolumeButton?.(video);
  }

  async #toggleFullscreen() {
    const dm = this.deferredMedia;
    const media = dm?.querySelector("video, iframe");
    if (!media) return;

    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
      return;
    }

    if (media.requestFullscreen) {
      try {
        await media.requestFullscreen();
      } catch {
        /* blocked by the browser; nothing to do */
      }
    } else if (media.webkitEnterFullscreen) {
      // iPhone Safari has no Element.requestFullscreen; use the native player.
      media.webkitEnterFullscreen();
    }
  }

  #onFullscreenChange = () => {
    const video = this.deferredMedia?.querySelector("video");
    if (!video) return;
    // The card hides native controls, but in fullscreen they're the only way
    // to scrub, change volume, or exit — so enable them just for fullscreen.
    video.controls = document.fullscreenElement === video;
  };

  #isMarketingPopupOpen() {
    const popup = document.querySelector(
      '#attentive_overlay, #attentive_creative, [id^="attentive_"], iframe[src*="attn.tv"], iframe[src*="attentivemobile"]'
    );
    if (!popup) return false;
    const style = window.getComputedStyle(popup);
    return style.display !== "none" && style.visibility !== "hidden";
  }

  #onVideoPause = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (!this.#explicitPlay || video.ended) return;

    // A click inside the card just before the pause means the user paused it.
    if (Date.now() - this.#lastClickAt < 400) return;

    if (!this.#isMarketingPopupOpen()) return;

    this.#pausedByPopup = true;

    // Resume right away if the card is still on screen, so the popup doesn't
    // interrupt playback. Capped in case something keeps forcing pauses.
    const rect = this.getBoundingClientRect();
    const onScreen = rect.bottom > 0 && rect.top < window.innerHeight;
    if (onScreen && this.#popupResumeAttempts < 5) {
      this.#popupResumeAttempts += 1;
      video.play()?.catch?.(() => {});
    }
  };

  #onBodyMutation = (mutations) => {
    if (!this.#pausedByPopup) return;
    const removedSomething = mutations.some((m) => m.removedNodes.length > 0);
    if (!removedSomething || this.#isMarketingPopupOpen()) return;

    // Popup closed: resume the video if it's still paused mid-play.
    this.#pausedByPopup = false;
    const video = this.deferredMedia?.querySelector("video");
    if (video && video.paused && !video.ended && this.#explicitPlay) {
      video.play()?.catch?.(() => {});
    }
  };

  #onCenterClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    this.#lastClickAt = Date.now();
    this.#pausedByPopup = false;
    this.#popupResumeAttempts = 0;

    if (target.closest('[ref="fullscreenButton"]')) {
      void this.#toggleFullscreen();
      return;
    }

    // Never override an explicit mute/unmute click.
    if (target.closest('[ref="toggleVolumeButton"]')) return;

    const dm = this.deferredMedia;
    if (!dm) return;

    const isPlayPauseButton = !!target.closest('[ref="toggleMediaButton"]');
    const isDedicatedControl = isPlayPauseButton || !!target.closest(".deferred-media__poster-button");

    const video = dm.querySelector("video");
    const wasPlaying = dm.isPlaying;
    const wasMuted = !video || video.muted;

    if (dm.hasAttribute("data-media-loaded") && !isDedicatedControl) {
      if (wasPlaying && wasMuted) {
        // Already playing silently (autoplay with no cover image, or a hover
        // preview): the first click on the video should unmute it, not pause it.
        this.#unmute(video);
        this.#explicitPlay = true;
        return;
      }

      // Otherwise clicking the video toggles play/pause like the small button.
      dm.toggleMedia?.();
    }

    // Remember whether this click was meant to play or pause, so mouseleave
    // knows not to auto-pause a deliberate click-to-play.
    this.#explicitPlay = !wasPlaying;

    // Unmute when a click starts playback. Skipped for the dedicated play/pause
    // button, since its pause is synchronous and would make "was playing" stale here.
    if (!wasPlaying && !isPlayPauseButton) {
      this.#unmute(video);
    }
  };
}

if (!customElements.get("video-card")) {
  customElements.define("video-card", VideoCard);
}


